// @flow

import {useSplit} from '@travel-frontend/split-io';
import {HeaderNew} from '@travel-frontend/ui-components';
import {isUserIdSet} from '@travel-frontend/user-id';
import {Node, useEffect, useState} from 'react';
import {Trans} from 'react-i18next';
import {useSelector} from 'react-redux';
import {Navigate, useNavigationType} from 'react-router-dom';

import {Footer, LoadingPageLayout} from 'components/Layout';
import {PageLoading as UIPageLoading} from 'components/PageLoading';
import {config} from 'config';
import {PAYPAL_SPLIT} from 'consts';
import {useBookStatus} from 'hooks/useBookStatus';
import {useDirectusHeader} from 'hooks/useDirectusHeader';
import {useTracking} from 'hooks/useTracking';
import {PaypalLayer} from 'modules/CheckoutPage/components/PaypalLayer';
import {Checkout as NavigateCheckout} from 'modules/Common/Navigate/Checkout';
import {
  getBookingInProcess,
  getJourneyId,
} from 'redux/uiState/layout/booking/reducer';
import {isJourneyLoaded} from 'redux/v1/journey/reducer';

export const PageLoading = (): Node => {
  const {trackPageView} = useTracking();
  const navigationType = useNavigationType();

  const journeyId = useSelector(getJourneyId);
  const {headerConfig} = useDirectusHeader();
  const journeyLoaded = useSelector(isJourneyLoaded);
  const userIdLoaded = useSelector(isUserIdSet);
  const paypalEnabled = useSplit(PAYPAL_SPLIT)?.treatment === 'on';

  const {bookingInProgress, booked, requestFailed} = useBookStatus(journeyId);
  const bookingInProcess = useSelector(getBookingInProcess);
  const loadingTextKey =
    bookingInProcess || bookingInProgress ? 'confirm' : 'checkout';
  const [loadingTracked, setIsLoadingTracked] = useState(false);

  const isReadyToTrack =
    journeyLoaded && userIdLoaded && loadingTextKey === 'confirm';

  useEffect(() => {
    if (
      (navigationType === 'REPLACE' || navigationType === 'PUSH') &&
      !loadingTracked &&
      isReadyToTrack
    ) {
      trackPageView(loadingTextKey, true);
      setIsLoadingTracked(true);
    }
  }, [
    loadingTextKey,
    loadingTracked,
    trackPageView,
    setIsLoadingTracked,
    isReadyToTrack,
    navigationType,
  ]);

  if (requestFailed) {
    return <Navigate to="/" replace />;
  }

  if (!bookingInProgress && booked) {
    return <NavigateCheckout path={`/confirmation/${journeyId}`} />;
  }

  return (
    <>
      <LoadingPageLayout>
        <HeaderNew
          config={{
            login: false,
            sslIcon: true,
            logo: headerConfig.logo,
          }}
        />
        <UIPageLoading
          title={
            <Trans
              i18nKey={`general:loading.${loadingTextKey}`}
              context="control"
            />
          }
          message={
            <Trans
              ns="general"
              i18nKey={`loading.${loadingTextKey}`}
              context="default"
            />
          }
        />
        <Footer kind={'shortWithLinks'} config={config.footer} />
      </LoadingPageLayout>
      {paypalEnabled && journeyLoaded && <PaypalLayer />}
    </>
  );
};
